// SCR-STAFF-317 و SCR-STAFF-318 (AP-MED-03؛ US-SCH-132 تا 134، US-INT-102): وضعیت ارسال ظرفیت ارجاعی به سیب، نوبت‌های
// اخذشده، ارسال دستی، حالت ارسال خودکار یا دستی و نمای سلامت همگام‌سازی ۲۰ دقیقه.
//
// فهرست و ارسال دستی `sch.referral_capacity.send` می‌خواهند (الزام حاکمیتی، بدون Feature) و تعیین حالت «یکی از
// `sch.schedule.manage` یا `sch.channel.manage`» (C-AGENT-1740).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  isApiError,
  unwrap,
  type SchReferralBookingListResponse,
  type SchReferralBookingView,
  type SchReferralCapacityHealthResponse,
  type SchReferralCapacityHealthView,
  type SchReferralPublicationListResponse,
  type SchReferralPublicationModeListResponse,
  type SchReferralPublicationModeView,
  type SchReferralPublicationView,
  type SchReferralPublishMode,
  type SchReferralPublishStatus,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  DetailPage,
  StatusChip,
  faDigits,
  toJalali,
  toJalaliDateTime,
  useDisplaySettings,
  type DataTableColumn,
  type DetailTab,
  type StatusTone,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/** وضعیت ارسال ظرفیت ارجاعی (SCR-STAFF-317). */
export const SCR_STAFF_317 = 'SCR-STAFF-317';

const STATUS_TONE: Record<SchReferralPublishStatus, StatusTone> = {
  NotSent: 'pending',
  Sent: 'done',
  Taken: 'booked',
};

export const referralCapacityKeys = {
  list: (tenantId: string | undefined, branchId: string, status: string) =>
    ['scheduling', tenantId, 'referral-capacity', branchId, status] as const,
  bookings: (tenantId: string | undefined, branchId: string) =>
    ['scheduling', tenantId, 'referral-bookings', branchId] as const,
  health: (tenantId: string | undefined) => ['scheduling', tenantId, 'referral-health'] as const,
  modes: (tenantId: string | undefined, branchId: string) => ['scheduling', tenantId, 'referral-modes', branchId] as const,
};

export function ReferralCapacityPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const branchId = session?.activeBranchId ?? session?.branchIds[0] ?? '';
  const [status, setStatus] = useState<'all' | SchReferralPublishStatus>('all');
  const [tab, setTab] = useState('capacity');
  const canSend = access.can('sch.referral_capacity.send');
  const canSetMode = access.can('sch.schedule.manage') || access.can('sch.channel.manage');

  const publications = useQuery({
    queryKey: referralCapacityKeys.list(session?.tenantId, branchId, status),
    enabled: canSend && Boolean(branchId),
    queryFn: async (): Promise<SchReferralPublicationView[]> => {
      const response = await unwrap(
        tenantApi.GET('/api/v1/scheduling/referral-capacity', {
          params: { query: { branchId, status: status === 'all' ? undefined : status } },
        }),
      );
      return (response as SchReferralPublicationListResponse).items;
    },
  });

  const bookings = useQuery({
    queryKey: referralCapacityKeys.bookings(session?.tenantId, branchId),
    enabled: canSend && Boolean(branchId),
    queryFn: async (): Promise<SchReferralBookingView[]> => {
      const response = await unwrap(
        tenantApi.GET('/api/v1/scheduling/referral-capacity/bookings', { params: { query: { branchId } } }),
      );
      return (response as SchReferralBookingListResponse).items;
    },
  });

  const health = useQuery({
    queryKey: referralCapacityKeys.health(session?.tenantId),
    enabled: canSend,
    queryFn: async (): Promise<SchReferralCapacityHealthView[]> => {
      const response = await unwrap(tenantApi.GET('/api/v1/scheduling/referral-capacity/health', {}));
      return (response as SchReferralCapacityHealthResponse).items;
    },
  });

  const modes = useQuery({
    queryKey: referralCapacityKeys.modes(session?.tenantId, branchId),
    enabled: canSetMode && Boolean(branchId),
    queryFn: async (): Promise<SchReferralPublicationModeView[]> => {
      const response = await unwrap(
        tenantApi.GET('/api/v1/scheduling/referral-capacity/modes', { params: { query: { branchId } } }),
      );
      return (response as SchReferralPublicationModeListResponse).items;
    },
  });

  const send = useMutation({
    mutationFn: (row: SchReferralPublicationView) =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/referral-capacity/{providerId}/{date}:send', {
          params: { path: { providerId: row.providerId, date: row.date } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error);
      void queryClient.invalidateQueries({ queryKey: referralCapacityKeys.list(session?.tenantId, branchId, status) });
      void queryClient.invalidateQueries({ queryKey: referralCapacityKeys.health(session?.tenantId) });
    },
  });

  const setMode = useMutation({
    mutationFn: (input: { providerId: string | null; mode: SchReferralPublishMode }) =>
      unwrap(
        tenantApi.PUT('/api/v1/scheduling/referral-capacity/modes', {
          body: { branchId, providerId: input.providerId, mode: input.mode },
        }),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: referralCapacityKeys.modes(session?.tenantId, branchId) });
      void queryClient.invalidateQueries({ queryKey: referralCapacityKeys.list(session?.tenantId, branchId, status) });
    },
  });

  // `missing` افزوده problem+json خطای PHX-SCH-0333 است و در `ApiError.extensions` می‌نشیند.
  const missingValue = isApiError(send.error) ? send.error.extensions['missing'] : undefined;
  // علت‌ها همان رشته‌های سرور (`academic_degree`، `specialty`، …) هستند و با همان کلیدهای علت ترجمه می‌شوند.
  const missing =
    typeof missingValue === 'string'
      ? missingValue
          .split(',')
          .map((reason) => t(`sch:referral_capacity_reason.${reason.trim()}`, { defaultValue: reason.trim() }))
          .join(t('common:list_separator'))
      : undefined;

  const capacityColumns: DataTableColumn<SchReferralPublicationView>[] = [
    {
      id: 'status',
      headerKey: 'sch:referral_capacity.status',
      cell: (row) => <StatusChip tone={STATUS_TONE[row.status]} labelKey={`sch:referral_capacity_status.${row.status}`} />,
    },
    { id: 'provider', headerKey: 'sch:referral_capacity.provider', cell: (row) => <span>{row.providerName}</span> },
    { id: 'date', headerKey: 'sch:referral_capacity.date', cell: (row) => <span>{toJalali(row.date)}</span> },
    { id: 'slots', headerKey: 'sch:referral_capacity.slots', cell: (row) => <span>{faDigits(row.slotCount)}</span> },
    { id: 'taken', headerKey: 'sch:referral_capacity.taken', cell: (row) => <span>{faDigits(row.takenCount)}</span> },
    {
      id: 'mode',
      headerKey: 'sch:referral_capacity.mode',
      cell: (row) => <span>{t(`sch:referral_capacity_mode.${row.mode}`)}</span>,
    },
    {
      id: 'reason',
      headerKey: 'sch:referral_capacity.reason',
      cell: (row) => (
        <span data-testid={`sch-referral-reason-${row.publicationId}`}>
          {row.reason ? t(`sch:referral_capacity_reason.${row.reason}`, { defaultValue: row.reason }) : '—'}
        </span>
      ),
    },
    {
      id: 'sent',
      headerKey: 'sch:referral_capacity.sent_at',
      cell: (row) => <span>{row.sentAt ? toJalaliDateTime(row.sentAt, { timeZone }) : '—'}</span>,
    },
    {
      id: 'actions',
      headerKey: 'sch:referral_capacity.send',
      cell: (row) =>
        row.status === 'NotSent' ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => send.mutate(row)}
            data-testid={`sch-referral-send-${row.publicationId}`}
          >
            {t('sch:referral_capacity.send')}
          </Button>
        ) : null,
    },
  ];

  const bookingColumns: DataTableColumn<SchReferralBookingView>[] = [
    { id: 'code', headerKey: 'sch:referral_capacity.referral_code', cell: (row) => <span>{row.referralCode}</span> },
    { id: 'date', headerKey: 'sch:referral_capacity.date', cell: (row) => <span>{toJalali(row.businessDate)}</span> },
    {
      id: 'start',
      headerKey: 'sch:referral_capacity.starts_at',
      cell: (row) => <span>{toJalaliDateTime(row.startsAt, { timeZone })}</span>,
    },
    {
      id: 'source',
      headerKey: 'sch:referral_capacity.source',
      cell: (row) => <span>{t(`sch:referral_capacity_source.${row.source}`, { defaultValue: row.source })}</span>,
    },
  ];

  const tabs: DetailTab[] = [
    {
      id: 'capacity',
      labelKey: 'sch:referral_capacity.tab_capacity',
      content: (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              select
              size="small"
              label={t('sch:referral_capacity.status')}
              value={status}
              onChange={(event) => setStatus(event.target.value as typeof status)}
              data-testid="sch-referral-status-filter"
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="all">{t('sch:referral_capacity.filter_all')}</MenuItem>
              {(['NotSent', 'Sent', 'Taken'] as SchReferralPublishStatus[]).map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`sch:referral_capacity_status.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          {missing ? (
            <Alert severity="error" data-testid="sch-referral-send-blocked">
              {t('sch:referral_capacity.send_blocked', { missing })}
            </Alert>
          ) : null}
          <DataTable
            rows={publications.data}
            columns={capacityColumns}
            getRowId={(row) => row.publicationId}
            isLoading={publications.isLoading}
            error={publications.error}
            onRetry={() => void publications.refetch()}
            emptyTitleKey="sch:referral_capacity.empty"
            captionKey="sch:referral_capacity.tab_capacity"
          />
        </Stack>
      ),
    },
    {
      id: 'bookings',
      labelKey: 'sch:referral_capacity.tab_bookings',
      content: (
        <DataTable
          rows={bookings.data}
          columns={bookingColumns}
          getRowId={(row) => row.appointmentId}
          isLoading={bookings.isLoading}
          error={bookings.error}
          onRetry={() => void bookings.refetch()}
          emptyTitleKey="sch:referral_capacity.bookings_empty"
          captionKey="sch:referral_capacity.tab_bookings"
        />
      ),
    },
    {
      id: 'health',
      labelKey: 'sch:referral_capacity.tab_health',
      content: (
        <Stack spacing={2}>
          <Alert severity="info" data-testid="sch-referral-sla-hint">
            {t('sch:referral_capacity.sla_hint')}
          </Alert>
          <DataTable
            rows={health.data}
            columns={[
              { id: 'mode', headerKey: 'sch:referral_capacity.mode', cell: (row) => <span>{t(`sch:referral_capacity_mode.${row.mode}`, { defaultValue: row.mode })}</span> },
              {
                id: 'last',
                headerKey: 'sch:referral_capacity.last_push',
                cell: (row) => <span>{row.lastSuccessfulPushAt ? toJalaliDateTime(row.lastSuccessfulPushAt, { timeZone }) : '—'}</span>,
              },
              {
                id: 'oldest',
                headerKey: 'sch:referral_capacity.oldest_unsent',
                cell: (row) => (
                  <span data-testid={`sch-referral-oldest-${row.branchId}`}>
                    {row.oldestUnsentAt ? toJalaliDateTime(row.oldestUnsentAt, { timeZone }) : '—'}
                  </span>
                ),
              },
              { id: 'notsent', headerKey: 'sch:referral_capacity.not_sent_count', cell: (row) => <span>{faDigits(row.notSentCount)}</span> },
              { id: 'sent', headerKey: 'sch:referral_capacity.sent_count', cell: (row) => <span>{faDigits(row.sentCount)}</span> },
              { id: 'taken', headerKey: 'sch:referral_capacity.taken', cell: (row) => <span>{faDigits(row.takenCount)}</span> },
            ]}
            getRowId={(row) => row.branchId}
            isLoading={health.isLoading}
            error={health.error}
            onRetry={() => void health.refetch()}
            emptyTitleKey="sch:referral_capacity.health_empty"
            captionKey="sch:referral_capacity.tab_health"
          />
        </Stack>
      ),
    },
  ];

  if (canSetMode) {
    tabs.push({
      id: 'modes',
      labelKey: 'sch:referral_capacity.tab_modes',
      content: (
        <Stack spacing={2} data-testid="sch-referral-modes">
          <Typography variant="body2">{t('sch:referral_capacity.modes_hint')}</Typography>
          {(modes.data ?? []).map((row) => (
            <TextField
              key={row.providerId ?? 'branch'}
              select
              size="small"
              label={row.providerId ? t('sch:referral_capacity.provider_mode') : t('sch:referral_capacity.branch_mode')}
              value={row.mode}
              onChange={(event) => setMode.mutate({ providerId: row.providerId, mode: event.target.value as SchReferralPublishMode })}
              data-testid={`sch-referral-mode-${row.providerId ?? 'branch'}`}
              sx={{ maxWidth: 320 }}
            >
              {(['Automatic', 'Manual'] as SchReferralPublishMode[]).map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`sch:referral_capacity_mode.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          ))}
        </Stack>
      ),
    });
  }

  return (
    <DetailPage
      screenId={SCR_STAFF_317}
      titleKey="sch:referral_capacity.page_title"
      subtitleKey="sch:referral_capacity.page_subtitle"
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
    />
  );
}
